import type { FC } from "react";
import { useState, useEffect, useMemo } from "react";
import { Select } from "flowbite-react";
import { HiOutlinePlus, HiTrash, HiPencilAlt } from "react-icons/hi";
import { useParameters } from "../../../hooks";
import { useWorkspace } from "../../../context/WorkspaceContext";
import { useCache } from "../../../context/CacheContext";
import {
  Button,
  TableSkeleton,
  StandardTable,
  DialogForm,
  ParameterModal,
  HelpLabel,
  StoryblokHelpBlock,
} from "@components";
import {
  ITableRow,
  IParameter,
  ParameterType,
  Timestamp,
} from "../../../types";
import { useTranslation } from "react-i18next";
import { Parameter } from "../../../models";
import { SettingsRowProps } from "../components/SettingsRow";

interface ResourceTypesSettingsContentProps {
  SettingsRow: React.ComponentType<SettingsRowProps>;
}

const ResourceTypesSettingsContent: FC<ResourceTypesSettingsContentProps> = ({
  SettingsRow,
}) => {
  const [isUpsertModalOpen, openUpsertodal] = useState(false);
  const [isDeleteModalOpen, openDeleteModal] = useState(false);
  const [parameterType, setParameterType] = useState(ParameterType.All);
  const [isLoading, setLoading] = useState(true);
  const [tableRows, setTableRows] = useState([] as ITableRow[]);
  const [parameterData, setParameterData] = useState(Parameter.default());
  const { activeWorkspace } = useWorkspace();
  const { updateCacheKey } = useCache();
  const query = useParameters(undefined, parameterType);
  const { t } = useTranslation(["common", "configuration"]);

  const isQueryLoading = useMemo(
    () => query.isLoading || query.isRefetching,
    [query],
  );

  const parameters = useMemo(() => query.data ?? [], [query]);

  const parameterOptions = useMemo(
    () => [
      {
        value: ParameterType.All,
        label: t("configuration:parameter_types.all"),
      },
      {
        value: ParameterType.VehicleType,
        label: t("configuration:parameter_types.vehicle"),
      },
      {
        value: ParameterType.ProductType,
        label: t("configuration:parameter_types.product"),
      },
    ],
    [t],
  );

  useEffect(() => {
    setLoading(isQueryLoading);
  }, [isQueryLoading]);

  useEffect(() => {
    if (!isQueryLoading && parameters.length > 0) {
      // Sort parameters by creation date (newest first)
      const sortedParameters = parameters.sort((a, b) => {
        if (!a.createdAt && !b.createdAt) return 0;
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt.seconds - a.createdAt.seconds;
      });

      setTableRows(
        sortedParameters.map((parameter) => {
          return {
            id: parameter.code,
            cells: [
              {
                id: "code",
                children: parameter.code,
                showOnSmallScreen: true,
              },
              {
                id: "type",
                children:
                  parameter.type === "VehicleType"
                    ? t("configuration:parameter_types.vehicle")
                    : parameter.type === "ProductType"
                      ? t("configuration:parameter_types.product")
                      : parameter.type,
                showOnSmallScreen: true,
              },
              {
                id: "createdAt",
                children: Timestamp.toDisplayDate(parameter.createdAt),
              },
            ],
            actions: [
              {
                id: "edit",
                icon: <HiPencilAlt />,
                color: "gray",
                onActionClick: () => {
                  setParameterData(parameter);
                  openUpsertodal(true);
                },
              },
              {
                id: "delete",
                icon: <HiTrash />,
                color: "failure",
                onActionClick: () => {
                  setParameterData(parameter);
                  openDeleteModal(true);
                },
              },
            ],
          };
        }),
      );
    } else if (!isQueryLoading) {
      setTableRows([]);
    }
  }, [parameters, isQueryLoading, t]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-text sm:text-2xl">
              {t("configuration:parameters")}
            </h2>
            <HelpLabel
              content={
                <StoryblokHelpBlock
                  storySlug="logibud-tms-web/help-popups/resource-type"
                  accessToken="Jf9NMAu4DXeo3fwdjd1w7wtt"
                />
              }
              className="mt-1"
            />
          </div>
          <p className="text-secondary text-base pt-2 pb-4 dark:text-secondary">
            {t("configuration:parameters_description")}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start gap-y-4 sm:gap-y-0 sm:gap-x-6 w-full sm:w-auto mt-4 sm:mt-0">
          <Select
            defaultValue={parameterType}
            onChange={(e) => {
              setParameterType(e.target.value as ParameterType);
            }}
            className="w-full sm:w-auto"
          >
            {parameterOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Button
            color="light"
            onClick={() => {
              setParameterData(Parameter.default());
              openUpsertodal(true);
            }}
            type="submit"
            className="w-full sm:w-auto whitespace-nowrap"
          >
            <HiOutlinePlus className="mr-2 h-5 w-5" />
            {t("configuration:new_parameter")}
          </Button>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden shadow text:white">
              {isLoading ? (
                <TableSkeleton />
              ) : (
                <StandardTable
                  header={[
                    {
                      id: "code",
                      children: t("configuration:fields.code"),
                      showOnSmallScreen: true,
                    },
                    {
                      id: "type",
                      children: t("configuration:fields.type"),
                      showOnSmallScreen: true,
                    },
                    {
                      id: "createdAt",
                      children: t("common:created_at"),
                    },
                  ]}
                  hasActions={true}
                  rows={tableRows}
                  compact={true}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <ParameterModal
        data={parameterData}
        isShowing={isUpsertModalOpen}
        onConfirm={(data: IParameter) => {
          if (Parameter.isNew(parameterData)) {
            Parameter.create(activeWorkspace?.workspaceId ?? "", data);
          } else {
            Parameter.update(parameterData, data);
          }
          updateCacheKey();
          openUpsertodal(false);
        }}
        onCancel={() => openUpsertodal(false)}
      />

      <DialogForm
        title={
          t("configuration:delete_parameter") + " " + parameterData.code + "?"
        }
        show={isDeleteModalOpen}
        toggleModal={() => openDeleteModal(false)}
        buttonConfirmColor="failure"
        dismissible
        confirmButton={() => {
          Parameter.remove(parameterData);
          updateCacheKey();
          openDeleteModal(false);
        }}
        buttonConfirmText={t("common:confirm")}
        buttonConfirmPrependIcon={<HiTrash className="mr-2 h-4 w-4" />}
        buttonCloseText={t("common:close")}
      />
    </div>
  );
};

export default ResourceTypesSettingsContent;
